<template>
    <transition name="fade">
        <div v-if="isVisible" :class="overlayClasses" :style="overlayStyle">
            <div class="loader-content">
                <b-spinner :variant="resolvedSpinnerVariant" :style="spinnerStyle" label="Loading" />
                <div v-if="message" class="loader-text">
                    {{ message }}
                </div>
            </div>
        </div>
    </transition>
</template>

<script>
    import { BSpinner } from 'bootstrap-vue-next';

    export default {
        name: 'FullPageLoader',
        components: {
            BSpinner,
        },
        props: {
            show: {
                type: Boolean,
                default: undefined,
            },
            active: {
                type: Boolean,
                default: undefined,
            },
            fullPage: {
                type: Boolean,
                default: undefined,
            },
            isFullPage: {
                type: Boolean,
                default: undefined,
            },
            message: {
                type: String,
                default: '',
            },
            spinnerVariant: {
                type: String,
                default: null,
            },
            spinnerColor: {
                type: String,
                default: null,
            },
            size: {
                type: String,
                default: '3rem',
            },
            width: {
                type: [String, Number],
                default: null,
            },
            height: {
                type: [String, Number],
                default: null,
            },
            canCancel: {
                type: Boolean,
                default: false,
            },
            container: {
                type: null,
                default: null,
            },
            overlayClass: {
                type: [String, Array, Object],
                default: '',
            },
            overlayColor: {
                type: String,
                default: 'rgba(255, 255, 255, 0.72)',
            },
            overlayBlur: {
                type: String,
                default: '1px',
            },
        },
        data() {
            return {
                parentPositionWasInjected: false,
            };
        },
        computed: {
            isVisible() {
                if (typeof this.show === 'boolean') {
                    return this.show;
                }

                if (typeof this.active === 'boolean') {
                    return this.active;
                }

                return false;
            },
            resolvedFullPage() {
                if (typeof this.fullPage === 'boolean') {
                    return this.fullPage;
                }

                if (typeof this.isFullPage === 'boolean') {
                    return this.isFullPage;
                }

                return true;
            },
            overlayClasses() {
                return [
                    'loader-overlay',
                    this.resolvedFullPage ? 'loader-overlay--full-page' : 'loader-overlay--local',
                    this.overlayClass,
                ];
            },
            resolvedSpinnerVariant() {
                if (this.spinnerColor !== null) {
                    return null;
                }

                return this.spinnerVariant !== null ? this.spinnerVariant : 'primary';
            },
            spinnerStyle() {
                return {
                    width: this.normalizeLength(this.width || this.size),
                    height: this.normalizeLength(this.height || this.width || this.size),
                    color: this.spinnerColor !== null ? this.spinnerColor : null,
                };
            },
            overlayStyle() {
                return {
                    backgroundColor: this.overlayColor,
                    backdropFilter: `blur(${this.overlayBlur})`,
                    WebkitBackdropFilter: `blur(${this.overlayBlur})`,
                };
            },
        },
        mounted() {
            this.syncParentPosition();
        },
        updated() {
            this.syncParentPosition();
        },
        beforeUnmount() {
            this.resetParentPosition();
        },
        methods: {
            normalizeLength(value) {
                return typeof value === 'number' ? `${value}px` : value;
            },
            syncParentPosition() {
                if (!this.isVisible || this.resolvedFullPage || !this.$el || !this.$el.parentElement) {
                    this.resetParentPosition();
                    return;
                }

                const parent = this.$el.parentElement;
                if (window.getComputedStyle(parent).position === 'static' && !parent.style.position) {
                    parent.style.position = 'relative';
                    this.parentPositionWasInjected = true;
                }
            },
            resetParentPosition() {
                if (!this.parentPositionWasInjected || !this.$el || !this.$el.parentElement) {
                    return;
                }

                this.$el.parentElement.style.position = '';
                this.parentPositionWasInjected = false;
            },
        },
    };
</script>

<style scoped>
    .loader-overlay {
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .loader-overlay--full-page {
        position: fixed;
        z-index: 9999;
    }

    .loader-overlay--local {
        position: absolute;
        z-index: 10;
        min-height: 100%;
    }

    .loader-content {
        text-align: center;
        color: #212529;
        padding: 1rem;
    }

    .loader-text {
        margin-top: 12px;
        font-size: 1rem;
        font-weight: 500;
    }

    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 0.2s ease;
    }

    .fade-enter,
    .fade-leave-to {
        opacity: 0;
    }
</style>
